import React, { lazy, Suspense } from "react";
import TaxFormImg1 from "@/assets/TaxForm/waggex-compliance-management-software-dashboard-india.webp?w=500&format=webp";

const CustomerReviews = lazy(
  () => import("../components/payrolewebapp/CustomerReviews"),
);

const ComparisonTable = lazy(
  () => import("../components/payrolewebapp/ComparisonTable"),
);

const HomeFaq = lazy(() => import("./HomeFaq"));

const AppDownloadCard = lazy(
  () => import("../components/common/AppDownloadCard"),
);

const PayrollBanner = lazy(() => import("../components/common/PayrollBanner"));

const ModernizeHR = lazy(() => import("../components/common/ModernizeHR"));

const DynamicFeaturesSection = lazy(
  () => import("../components/common/DynamicFeaturesSection"),
);

import { Calculator, FileClock, FileX2, Play, ReceiptText } from "lucide-react";

import StatsSection from "../components/common/StatsSection";
import { Helmet } from "react-helmet-async";
import { useHrmsCounts } from "../hooks/useHrmsCounts";
import { useInView } from "react-intersection-observer";
const taxItems = [
  {
    title: "Employee Compliance Records",
    description:
      "Store and manage employee compliance records, declarations, and statutory documents from one centralized dashboard for better accessibility and organization.",
    icon: (
      <div className="text-[#10324F]">
        <Play fill="currentColor" size={16} aria-hidden="true" />
      </div>
    ),
  },
  {
    title: "PF & ESI Management",
    description:
      "Maintain PF and ESI records in a structured system that helps businesses organize compliance-related employee information efficiently.",
    icon: (
      <div className="text-[#10324F]">
        <Play fill="currentColor" size={16} aria-hidden="true" />
      </div>
    ),
  },
  {
    title: "Payroll Compliance",
    description:
      "Organize payroll-related compliance forms, records, and documentation in one place for easier tracking and management.",
    icon: (
      <div className="text-[#10324F]">
        <Play fill="currentColor" size={16} aria-hidden="true" />
      </div>
    ),
  },
  {
    title: "Audit & Document Access",
    description:
      "Access compliance documents instantly during audits, inspections, and internal reviews with centralized digital record management.",
    icon: (
      <div className="text-[#10324F]">
        <Play fill="currentColor" size={16} aria-hidden="true" />
      </div>
    ),
  },
];
const taxbasedFeatures = [
  // ROW 1
  {
    id: 1,
    title: "TDS Record Management",
    desc: "Manage TDS records, deduction details, and employee tax information from one dashboard.",
  },
  {
    id: 2,
    title: "PF & ESI Records",
    desc: "Maintain PF and ESI records with contribution details and compliance visibility.",
  },
  {
    id: 3,
    title: "Form Management",
    desc: "Store employee declarations, statutory forms, and payroll compliance documents securely.",
  },

  // ROW 2
  {
    id: 4,
    title: "Employee Tax Details",
    desc: "Track employee tax declarations, exemptions, deductions, and investment records easily.",
  },
  {
    id: 5,
    title: "Allowance & Deduction Records",
    desc: "Manage allowances, deductions, benefits, and salary components in one centralized system.",
  },
  {
    id: 6,
    title: "Payroll Integration",
    desc: "Connect compliance records directly with payroll processing and payslip generation workflows.",
  },

  // ROW 3
  {
    id: 7,
    title: "Document Organization",
    desc: "Organize compliance documents, tax records, and statutory forms for quick access.",
  },
  {
    id: 8,
    title: "Payroll-Ready Compliance",
    desc: "Review compliance records and statutory data before payroll processing and approvals.",
  },
  {
    id: 9,
    title: "Payroll-Ready Data",
    desc: "Maintain accurate employee records for payroll calculations, reporting, and compliance reviews.",
  },
];
const headers = [
  "COMPLIANCE ACTIVITY",
  "MANUAL COMPLIANCE WORK",
  "WAGGEX COMPLIANCE MANAGEMENT",
];

const rows = [
  {
    feature: "Employee Records",
    without: "Scattered across files",
    with: "Centralized digital records",
  },
  {
    feature: "Tax Documentation",
    without: "Manual record tracking",
    with: "Organized compliance records",
  },
  {
    feature: "PF & ESI Records",
    without: "Difficult to maintain",
    with: "Structured record management",
  },
  {
    feature: "Form Management",
    without: "Paper-based processes",
    with: "Centralized form storage",
  },
  {
    feature: "Payroll Compliance",
    without: "Manual documentation",
    with: "Organized compliance visibility",
  },
  {
    feature: "Reports",
    without: "Time-consuming preparation",
    with: "Compliance-ready reports",
  },
  {
    feature: "Record Access",
    without: "Difficult retrieval",
    with: "Instant document access",
  },
  {
    feature: "Audit Preparation",
    without: "Manual compilation",
    with: "Better audit readiness",
  },
];
const taxFaqs1 = [
  {
    title: "What is Waggex Compliance Management Software?",
    content:
      "Waggex Compliance Management Software helps businesses organize payroll compliance records, statutory forms, employee documents, and tax-related information from one centralized platform.",
  },
  {
    title: "Is My Data Secure With The Waggex Payroll Web App?",
    content:
      "Yes. The Waggex App enforces Grade SSL Encryption, Multi-Factor Authentication, and Encrypted Backups to protect all business data. Compliance records, statutory forms, and employee documents are stored securely with role-based access controls so only authorised users can view or make changes.",
  },
  {
    title: "Can I Create More Than One Company On Waggex?",
    content:
      "Yes. Waggex supports multiple company accounts under a single login. Each company operates independently with its own compliance settings, employee records, and payroll configurations. Switch between accounts from the same dashboard without logging out.",
  },
];

const taxFaqs2 = [
  {
    title: "Can Waggex help manage PF and ESI records?",
    content:
      "Yes. Waggex helps businesses maintain PF and ESI records with better organization and accessibility from one centralized compliance dashboard.",
  },
  {
    title: "Does the software support payroll compliance?",
    content:
      "Yes. Waggex allows businesses to manage payroll-related compliance records, forms, and documentation in a structured and accessible system.",
  },
  {
    title: "Can compliance records be accessed during audits?",
    content:
      "Yes. Compliance documents and records are stored centrally for quick access during audits, inspections, and internal reviews.",
  },
];

const schema = {
  "@context": "https://schema.org",
  "@graph": [
    // 1. COMPLIANCE MANAGEMENT SOFTWARE
    {
      "@type": "SoftwareApplication",
      name: "Waggex Compliance Management Software",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web, Android, iOS",
      description:
        "Waggex Compliance Management Software helps Indian businesses manage payroll compliance, PF, ESI, TDS, statutory records, employee forms, and tax documents from one centralized platform.",
      url: "https://www.waggex.com/tax-form-management",
      featureList: [
        "Payroll compliance software India",
        "PF ESI compliance software India",
        "Statutory compliance management software India",
        "Tax and form management software India",
        "Employee compliance tracking software India",
        "Compliance management system India",
        "Regulatory compliance management software India",
      ],
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "INR",
      },
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.8",
        ratingCount: "187",
        bestRating: "5",
        worstRating: "1",
      },
    },

    // 2. FAQ PAGE
    {
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "What is Waggex Compliance Management Software?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Waggex Compliance Management Software helps businesses organize payroll compliance records, statutory forms, employee documents, and tax-related information from one centralized platform.",
          },
        },
        {
          "@type": "Question",
          name: "Can Waggex help manage PF and ESI records?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. Waggex helps businesses maintain PF and ESI records with better organization and accessibility from one centralized compliance dashboard.",
          },
        },
        {
          "@type": "Question",
          name: "Does the software support payroll compliance?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. Waggex allows businesses to manage payroll-related compliance records, forms, and documentation in a structured and accessible system.",
          },
        },
        {
          "@type": "Question",
          name: "Can compliance records be accessed during audits?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. Compliance documents and records are stored centrally for quick access during audits, inspections, and internal reviews.",
          },
        },
        {
          "@type": "Question",
          name: "Is document storage included?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. Waggex provides centralized storage for compliance forms, employee declarations, and payroll records.",
          },
        },
      ],
    },

    // 3. ORGANIZATION
    {
      "@type": "Organization",
      name: "Waggex",
      url: "https://www.waggex.com/",
      logo: "https://www.waggex.com/waggex-payroll-software-logo.png",
      description:
        "Waggex provides compliance management software and complete HRMS solutions for Indian businesses.",
      areaServed: "IN",
    },
  ],
};
const taxbasedProblems = [
  {
    icon: Calculator,
    title: "Tax Calculation Errors",
    desc: "Manual tracking increases the risk of missing tax-related deadlines and compliance requirements.",
  },
  {
    icon: FileClock,
    title: "Delayed Reports",
    desc: "Compliance records spread across files and spreadsheets can create reporting delays and inaccuracies.",
  },
  {
    icon: FileX2,
    title: "Missed Forms",
    desc: "Important employee forms and statutory documents become difficult to manage and retrieve.",
  },
  {
    icon: ReceiptText,
    title: "Wrong Deductions",
    desc: "Managing multiple compliance obligations manually increases administrative workload and errors.",
  },
];
export default function TaxFormsManagement() {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const { counts, loading } = useHrmsCounts(inView);
  return (
    <>
      <Helmet>
        <title>
          Best Compliance Management Software For Payroll & HR Teams
        </title>
        <meta
          name="description"
          content="Manage PF, ESI, TDS, payroll compliance, and statutory records with compliance management software built for Indian businesses."
        />
        <meta
          name="keywords"
          content="compliance management software,best compliance management software,regulatory compliance management software,payroll compliance software India,statutory compliance management software,PF ESI compliance software,tax and form management software India,employee compliance tracking software,compliance management system India
"
        />
        <link
          rel="canonical"
          href="https://www.waggex.com/tax-form-management"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema),
          }}
        />
      </Helmet>
      <section className="w-full bg-[#d1dfea] overflow-hidden py-10 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-4">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              {/* Heading */}
              <h1 className="text-[#10324F] font-bold uppercase tracking-wide text-xl md:text-3xl">
                Compliance Management Software
              </h1>

              {/* Description */}
              <p className="service-list-item-text py-4 max-w-md sm:max-w-2xl mx-auto mb-6">
                Waggex Compliance Management Software helps Indian businesses
                manage payroll compliance, statutory records, tax documents, PF,
                ESI, TDS, and employee forms from one centralized platform. Stay
                compliant, reduce manual work, and keep records audit-ready
              </p>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button
                  onClick={() =>
                    (window.location.href = "https://app.waggex.com/signup")
                  }
                  className="w-full sm:w-auto bg-[#10324F] hover:bg-[#10324F] transition-all duration-300 text-white px-14 py-3 rounded-xl shadow-md text-sm sm:text-base font-medium"
                >
                  Start Free Today
                </button>

                <button
                  onClick={() => (window.location.href = "/contact-us")}
                  className="w-full sm:w-auto border border-[#10324F] text-[#10324F] hover:bg-[#10324F] hover:text-white transition-all duration-300 px-14 py-3 rounded-xl text-sm sm:text-base font-medium"
                >
                  Book a Demo
                </button>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative flex justify-center items-center">
              <picture>
                <source media="(max-width: 639px)" srcSet={TaxFormImg1} />
                <img
                  src={TaxFormImg1}
                  fetchPriority="high"
                  width={500}
                  height={412}
                  loading="eager"
                  decoding="async"
                  alt="Waggex compliance management software dashboard showing PF ESI TDS payroll compliance statutory records and employee forms for Indian businesses"
                  className="relative object-contain"
                />
              </picture>
            </div>
          </div>
        </div>
      </section>
      <section ref={ref}>
        <StatsSection counts={counts} loading={loading} />
      </section>
      <section className="bg-[#FFFFFF] py-10 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-xl sm:text-3xl font-roboto font-bold text-[#10324F] leading-tight mx-auto">
            Compliance Management Shouldn't Depend on Spreadsheets
          </h2>

          <p className="mt-5 text-sm sm:text-base text-[#10324F] max-w-4xl mx-auto leading-relaxed mb-10">
            Manual compliance tracking often results in missed deadlines,
            incomplete records, filing errors, and unnecessary compliance risks.
            Waggex helps businesses organize payroll compliance and statutory
            documentation from one system.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {taxbasedProblems.map((item, index) => {
              const Icon = item.icon;

              return (
                <div
                  key={index}
                  className="flex gap-4 p-4 rounded-xl border bg-white border-white"
                  style={{
                    boxShadow: `
                  rgb(167 191 210 / 40%) -15px -15px 20px inset,
                  rgb(170 188 202 / 30%) -20px -20px 30px
                `,
                  }}
                >
                  {/* ICON */}
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#10324F] text-white">
                    <Icon size={22} aria-hidden="true" />
                  </div>

                  {/* CONTENT */}
                  <div>
                    <h2 className="font-roboto text-[#10324F] font-semibold text-sm md:text-base text-left">
                      {item.title}
                    </h2>
                    <p className="font-roboto text-[#10324F] text-sm mt-1 text-left max-w-xs">
                      {item.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <section className="w-full bg-[#F6F9FB] py-10 px-6 ">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            {/* Left Content */}
            <div>
              <h2 className="font-roboto text-[#10324F] text-xl sm:text-3xl font-bold leading-tight">
                Manage Compliance, Statutory Records & Payroll Documents in One
                Place
              </h2>

              <p className="mt-5 text-[#10324F] text-base sm:text-lg leading-7">
                Waggex helps businesses centralize compliance activities,
                employee records, payroll documents, and statutory reporting for
                better visibility and control.
              </p>

              <button
                onClick={() =>
                  (window.location.href = "https://app.waggex.com/signup")
                }
                className="mt-8 bg-[#10324F] hover:bg-[#10324F] text-white 
          font-semibold px-14 py-4 rounded-xl shadow-lg 
          transition-all duration-300"
              >
                Get Tax & Form Management
              </button>
            </div>

            {/* Right Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Card 01 */}
              <div
                className="bg-white rounded-2xl p-6 border border-[#AEAEC014]"
                style={{
                  boxShadow: `
              rgb(167 191 210 / 40%) -15px -15px 20px inset,
              rgb(170 188 202 / 30%) -20px -20px 30px
            `,
                }}
              >
                <h2 className="text-[#10324F] text-3xl font-bold">01</h2>

                <p className="mt-4 text-[#10324F] leading-7 text-sm sm:text-base">
                  Store payroll and compliance records securely in one
                  centralized dashboard.
                </p>
              </div>

              {/* Card 02 */}
              <div
                className="bg-white rounded-2xl p-6 border border-[#AEAEC014]"
                style={{
                  boxShadow: `
              rgb(167 191 210 / 40%) -15px -15px 20px inset,
              rgb(170 188 202 / 30%) -20px -20px 30px
            `,
                }}
              >
                <h2 className="text-[#10324F] text-3xl font-bold">02</h2>

                <p className="mt-4 text-[#10324F] leading-7 text-sm sm:text-base">
                  Track PF, ESI, TDS, and statutory compliance requirements more
                  efficiently.
                </p>
              </div>

              {/* Card 03 */}
              <div
                className="bg-white rounded-2xl p-6 border border-[#AEAEC014]"
                style={{
                  boxShadow: `
              rgb(167 191 210 / 40%) -15px -15px 20px inset,
              rgb(170 188 202 / 30%) -20px -20px 30px
            `,
                }}
              >
                <h2 className="text-[#10324F] text-3xl font-bold">03</h2>

                <p className="mt-4 text-[#10324F] leading-7 text-sm sm:text-base">
                  Maintain employee forms and compliance documents with
                  structured organization.
                </p>
              </div>

              {/* Card 04 */}
              <div
                className="bg-white rounded-2xl p-6 border border-[#AEAEC014]"
                style={{
                  boxShadow: `
              rgb(167 191 210 / 40%) -15px -15px 20px inset,
              rgb(170 188 202 / 30%) -20px -20px 30px
            `,
                }}
              >
                <h2 className="text-[#10324F] text-3xl font-bold">04</h2>

                <p className="mt-4 text-[#10324F] leading-7 text-sm sm:text-base">
                  Access compliance information quickly during audits, reviews,
                  and reporting activities.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Suspense fallback={null}>
        <DynamicFeaturesSection
          heading="Stay Compliant, Organized & Audit-Ready"
          description="Waggex helps HR and payroll teams manage tax records, statutory data, employee declarations, payroll deductions, and compliance-ready data from one platform"
          dynamicFeatures={taxbasedFeatures}
        />
        <section className="bg-[#F6F9FB] py-10 px-6 ">
          <div className="max-w-6xl mx-auto text-center">
            {/* Heading */}
            <h2 className="font-roboto text-xl md:text-3xl font-bold text-[#10324F]">
              Manage Critical Compliance Requirements With Confidence
            </h2>

            <p className="font-roboto mt-4 max-w-3xl mx-auto text-[#10324F] text-sm md:text-base leading-7">
              Waggex helps businesses organize compliance records, statutory
              documents, and payroll-related information across multiple
              compliance areas
            </p>

            {/* Cards */}
            <div className="mt-14 grid grid-cols-1 sm:grid-cols-4 gap-4 text-left max-w-5xl mx-auto">
              {taxItems.map((item, index) => (
                <div
                  key={index}
                  className="flex gap-4 bg-white border-white px-4 py-6 rounded-xl"
                  style={{
                    boxShadow: `
                        rgb(167 191 210 / 40%) -15px -15px 20px inset,
                        rgb(170 188 202 / 30%) -20px -20px 30px
                      `,
                  }}
                >
                  {/* Icon */}
                  <div className="mt-1 shrink-0 text-[#10324F]">
                    {item.icon}
                  </div>

                  {/* Content */}
                  <div>
                    <h2 className="text-sm font-roboto font-semibold text-[#10324F]">
                      {item.title}
                    </h2>

                    <p className="mt-2 font-roboto text-[#10324F] leading-7 text-xs">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <ComparisonTable
          headers={headers}
          rows={rows}
          title="Waggex Compliance Management vs Manual Compliance Tracking"
          subtitle="See how structured compliance management reduces filing risks and improves record visibility for Indian businesses"
          source="payrollfeature"
        />
        <CustomerReviews />
        {/* <FAQ /> */}
        <HomeFaq
          faq1={taxFaqs1}
          faq2={taxFaqs1}
          subtitle="Common questions from Indian businesses evaluating Waggex Compliance Management Software"
        />
        <PayrollBanner
          heading="Manage Compliance & Payroll From One Connected System"
          description="Simplify payroll processing, employee management, and compliance record tracking with one integrated platform built for Indian businesses"
          buttonText="View Payroll Software"
        />
        <AppDownloadCard />
        <ModernizeHR
          heading="Simplify Payroll Processing With Waggex"
          description="Run accurate, compliant payroll every month using payroll management software built for the way Indian businesses actually operate. No spreadsheets. No errors. Start free today."
        />
      </Suspense>
    </>
  );
}
